import { Alert } from '@apex/component-library'
import { Box, HStack, Icon, Link, SimpleGrid, Stack } from '@chakra-ui/react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FaArrowLeft } from 'react-icons/fa'
import { connect, ConnectedProps } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { Link as ReactRouterLink, useHistory } from 'react-router-dom'
import { Dispatch } from 'redux'
import { isEmpty } from 'lodash'
import { searchTransfers as searchTransfersApi } from '../../api/acatsSearchApi'
import { useClientRect, useToastErrorHandler } from '../../hooks'
import { RootState } from '../../store'
import { AccountRestrictionCodeDisplay } from '../../types'
import { RoutesEnum } from '../../types/RoutesEnum'
import { LayoutContext } from '../contexts/layout/LayoutContext'
import AccountInformation from './components/AccountInformation'
import AuditTrail from './components/AuditTrail'
import DocumentsSection from './components/Documents'
import MarginDetails from './components/MarginDetails'
import StatusTracker from './components/StatusTracker'
import { TransferDetailsKeyReferenceBar } from './components/TransferDetailsKeyReferenceBar'
import {
  caseStatus,
  clearAssets,
  clearDocuments,
  clearTransferDetails,
  getAuditTrail,
  getDocuments,
  getMarginDetails,
  getTransferDetails,
  loadAssets,
} from '../../actions'
import { AlpsAcatDetailV2WebDto as ITransferDetails } from '../../api/types'
import {
  AssetsTable,
  ExportTransferDetailsLink,
  TransferDetails,
} from './components/TransferDetails'
import {
  PROCESSING_DIRECTION_INTERNAL,
  PROCESSING_DIRECTION_JOINING,
  PROCESSING_DIRECTION_LEAVING,
} from './components/TransferDetails/constants'
import { deliveringAssetsSelector, receivingAssetsSelector } from './selectors'

interface MatchParams {
  acatControlNumber: string
}

export interface TransferDetailsPageComponentProps
  extends RouteComponentProps<MatchParams>,
    PropsFromRedux {}

const TransferDetailsPageComponent = ({
  auditTrail,
  documents,
  getAuditTrail,
  getCaseStatus,
  getDocuments,
  clearDocuments,
  getMarginDetails,
  getTransferDetails,
  clearTransferDetails,
  loadingAuditTrail,
  loadingDocuments,
  loadingMarginDetails,
  loadingTransferDetails,
  marginDetails,
  match,
  deliveringAssets,
  receivingAssets,
  loadingAssets,
  transferDetails,
  restrictionCode,
  loadAssets,
  clearAssets,
}: TransferDetailsPageComponentProps) => {
  const history = useHistory()
  const layout = useContext(LayoutContext)
  const [keyReferenceBarRect, keyReferenceBarRef] = useClientRect()
  const onError = useToastErrorHandler()

  useEffect(() => {
    const {
      location: { pathname },
    } = history
    const pathParts = pathname.split(`/`)
    const lastPathTerm = pathParts[pathParts.length - 1]
    getCaseStatus(lastPathTerm)
  }, [getCaseStatus, history])

  const acatsControlNumber = match.params.acatControlNumber

  let accountNumber: string | null | undefined
  switch (transferDetails?.direction) {
    case PROCESSING_DIRECTION_JOINING:
    case PROCESSING_DIRECTION_INTERNAL:
      accountNumber = transferDetails?.receiver.accountNumber?.replace('-', '')
      break
    case PROCESSING_DIRECTION_LEAVING:
      accountNumber = transferDetails?.deliverer?.accountNumber?.replace(
        '-',
        ''
      )
      break
    default:
      break
  }

  const loadTransferDetails = (acatsControlNumber: string) => {
    getTransferDetails(acatsControlNumber, onError)
  }

  const loadTransferDetailsMemo = useCallback(loadTransferDetails, [
    acatsControlNumber,
  ])

  useEffect(() => {
    loadTransferDetailsMemo(acatsControlNumber)
  }, [loadTransferDetailsMemo, acatsControlNumber])

  const loadMarginDetails = (
    accountNumber: string,
    acatsControlNumber: string
  ) => {
    getMarginDetails(accountNumber, acatsControlNumber)
  }

  const loadMarginDetailsMemo = useCallback(loadMarginDetails, [
    accountNumber,
    acatsControlNumber,
  ])

  useEffect(() => {
    accountNumber && loadMarginDetailsMemo(accountNumber, acatsControlNumber)
  }, [acatsControlNumber, loadMarginDetailsMemo, accountNumber])

  const loadDocumentsMemo = useCallback(
    (accountNumber: string) => {
      getDocuments(accountNumber, 'SCANDOCS')
    },
    [getDocuments]
  )

  useEffect(() => {
    accountNumber && loadDocumentsMemo(accountNumber)
  }, [loadDocumentsMemo, accountNumber])

  useEffect(() => {
    if (transferDetails) {
      getAuditTrail(transferDetails.processingCaseId)
      loadAssets(transferDetails, onError)
    }
  }, [transferDetails, getAuditTrail, loadAssets, onError])

  const [detailsAccountNumber, setDetailsAccountNumber] = useState<
    string | null
  >(null)

  /* Temporary until Alps returns account number in details. */
  useEffect(() => {
    searchTransfersApi({ acatControlNumber: acatsControlNumber }).then(
      ({ data }) => {
        setDetailsAccountNumber(data.acats[0].accountNumber)
      }
    )
  }, [acatsControlNumber])

  useEffect(() => {
    return () => {
      clearTransferDetails()
      clearAssets()
      clearDocuments()
    }
  }, [clearTransferDetails, clearAssets, clearDocuments])
  return (
    <Stack direction="column" spacing={0}>
      <Box ref={keyReferenceBarRef}>
        <TransferDetailsKeyReferenceBar
          detailsAccountNumber={detailsAccountNumber}
        />
      </Box>
      <Stack
        direction="column"
        spacing={4}
        padding={4}
        overflowY="auto"
        height={`calc(100vh - ${
          layout.headerHeight +
          layout.footerHeight +
          (keyReferenceBarRect?.height || 0)
        }px)`}
      >
        <HStack justifyContent="space-between">
          <Link
            as={ReactRouterLink}
            color="apex.link"
            to={{
              pathname: `/${RoutesEnum.TransferReview}`,
              search: history.location.search,
            }}
          >
            <Icon as={FaArrowLeft} /> Back to Results
          </Link>
          <Box>
            {transferDetails?.anonymousStatus.publicStatus && (
              <StatusTracker
                currentStatus={transferDetails.anonymousStatus.publicStatus}
              />
            )}
          </Box>
          <ExportTransferDetailsLink
            assets={[...deliveringAssets, ...receivingAssets]}
            auditTrail={auditTrail}
            transferDetails={transferDetails}
            loadingAssets={loadingAssets}
            loadingAuditTrail={loadingAuditTrail}
            loadingTransferDetails={loadingTransferDetails}
          />
        </HStack>
        {restrictionCode && (
          <Alert
            status="error"
            title="This account is restricted."
            description={AccountRestrictionCodeDisplay[restrictionCode]}
            isClosable={false}
            showIcon
          />
        )}
        {!loadingTransferDetails && transferDetails && (
          <TransferDetails transferDetail={transferDetails} />
        )}
        <SimpleGrid columns={2} spacing={4}>
          {!loadingTransferDetails && transferDetails && (
            <AccountInformation transferDetail={transferDetails} />
          )}
          {!loadingMarginDetails && marginDetails && (
            <MarginDetails marginDetails={marginDetails} />
          )}
        </SimpleGrid>
        {!loadingAssets && !isEmpty(deliveringAssets) && (
          <AssetsTable title="Delivering Assets" assets={deliveringAssets} />
        )}
        {!loadingAssets && !isEmpty(receivingAssets) && (
          <AssetsTable title="Non-delivering Assets" assets={receivingAssets} />
        )}
        {documents && !loadingDocuments && (
          <DocumentsSection documents={documents} />
        )}
        {auditTrail && !loadingAuditTrail && (
          <AuditTrail auditTrail={auditTrail} />
        )}
      </Stack>
    </Stack>
  )
}

const mapStateToProps = (state: RootState) => ({
  transferDetails: state.transferDetailsReducer.transferDetails,
  loadingTransferDetails: state.transferDetailsReducer.loadingTransferDetails,
  marginDetails: state.marginDetailsReducer.marginDetails,
  loadingMarginDetails: state.marginDetailsReducer.loadingMarginDetails,
  documents: state.documentsReducer.documents,
  loadingDocuments: state.documentsReducer.loadingDocuments,
  transferSearchState: state.transfersReducer,
  auditTrail: state.auditTrailReducer.auditTrail,
  loadingAuditTrail: state.auditTrailReducer.loadingAuditTrail,
  deliveringAssets: deliveringAssetsSelector(state),
  receivingAssets: receivingAssetsSelector(state),
  loadingAssets: state.assetsReducer.loadingAssets,
  restrictionCode: state.accountDetailsReducer.accountDetails?.restrictionCode,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getTransferDetails: (
    acatsControlNumber: string,
    onError: (error: string) => void
  ) => getTransferDetails(dispatch, acatsControlNumber, onError),
  clearTransferDetails: () => clearTransferDetails(dispatch),
  getMarginDetails: (acountNumber: string, acatsControlNumber: string) =>
    getMarginDetails(dispatch, acountNumber, acatsControlNumber),
  getDocuments: (acountNumber: string, documentTypes: string) =>
    getDocuments(dispatch, acountNumber, documentTypes),
  clearDocuments: () => clearDocuments(dispatch),
  getCaseStatus: (acatId: string) => caseStatus(dispatch, acatId),
  getAuditTrail: (processingCaseId: number) =>
    getAuditTrail(dispatch, processingCaseId),
  loadAssets: (
    transferDetails: ITransferDetails,
    onError: (error: string) => void
  ) => loadAssets(dispatch, transferDetails, onError),
  clearAssets: () => clearAssets(dispatch),
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export const TransferDetailsPage = connector(TransferDetailsPageComponent)
